import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import Logo from '../components/Logo';

const RegisterPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register } = useAuth();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(t('auth.passwordsNoMatch'));
      return;
    }

    if (password.length < 6) {
      toast.error('Password min 6 characters');
      return;
    }

    setLoading(true);

    try {
      const user = await register(name, email, password, phone);
      toast.success(`Welcome, ${user.name}!`);
      navigate('/');
    } catch (error) {
      if (error.message && error.message.includes('postMessage')) return;
      toast.error(error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 pb-56">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex justify-center mb-4">
            <Logo size="large" showTagline={true} />
          </Link>
        </div>

        <Card className="bg-[#141417] border-[#27272A]">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center"><h1>{t('auth.createAccount')}</h1></CardTitle>
            <CardDescription className="text-center text-zinc-400">
              {t('auth.createAccountDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('auth.name')}</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder={t('auth.namePlaceholder')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="bg-[#0B0B0C] border-[#27272A] focus:border-[#FFC107]"
                  data-testid="register-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-[#0B0B0C] border-[#27272A] focus:border-[#FFC107]"
                  data-testid="register-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">{t('auth.phone')}</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+41 91 923 14 28"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="bg-[#0B0B0C] border-[#27272A] focus:border-[#FFC107]"
                  data-testid="register-phone"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="bg-[#0B0B0C] border-[#27272A] focus:border-[#FFC107] pr-10"
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="bg-[#0B0B0C] border-[#27272A] focus:border-[#FFC107]"
                  data-testid="register-confirm-password"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#FFC107] text-black hover:bg-[#F59E0B] font-bold uppercase tracking-wide"
                data-testid="register-submit"
              >
                {loading ? t('auth.signingUp') : t('auth.createAccount')}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-zinc-400">{t('auth.hasAccount')} </span>
              <Link to="/login" className="text-[#FFC107] hover:underline font-medium" data-testid="login-link">
                {t('auth.signIn')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
